import React, { useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";

import {
  useGetGeoMeasurementsDevices,
  useGetMeasurementDevicesByTags,
  useGetMeasurementDevicesReadings,
} from "api/api-hooks/measurement-devices-hooks";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { defaultIcon } from "components/pages/measurement-devices/components/ObjectMapContent";
import L, { LatLngTuple, divIcon } from "leaflet";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Marker, Popup, ZoomControl, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { roundIfDecimal } from "utils/utils";
import DeviceDialogContent from "../components/DeviceDialogContent";

const measurementOptions = [
  { label: "None", value: "NONE" },
  { label: "Supply temperature", value: "SUPPLY_TEMPERATURE" },
  { label: "Return Temperature", value: "RETURN_TEMPERATURE" },
  { label: "Supply pressure", value: "SUPPLY_PRESSURE" },
  { label: "Return Pressure", value: "RETURN_PRESSURE" },
  { label: "Flow rate", value: "FLOW_RATE" },
  { label: "Power", value: "POWER" },
  { label: "Hot water temperature", value: "HOT_WATER_TEMPERATURE" },
];


type ExtractedDatasetsProps = {
  id: number;
  datasets: string[];
};

const MapContent = () => {
  const [selectedMeasurementType, setSelectedMeasurementType] = useState<string>("NONE");
  const [selectedTitle, setSelectedTitle] = useState<string>("");
  const [displayDialog, setDisplayDialog] = useState<boolean>(false);
  const [devicesIds, setDevicesIds] = useState<number[]>([]);
  const [extractedDatasets, setExtractedDatasets] = useState<ExtractedDatasetsProps[]>([]);
  const [requiredDatasets, setRequiredDatasets] = useState<string[]>([]);
  const map = useMap();

  const { data: devicesReadings, error: readingsError, isLoading } = useGetMeasurementDevicesReadings(
    selectedMeasurementType,
    devicesIds?.join(","),
    {
      enabled: devicesIds?.length > 0,
    }
  );

  const { data: devicesGeoCoordinates, error: geoError } = useGetGeoMeasurementsDevices();
  const { data: devicesByTags, error: tagsError } = useGetMeasurementDevicesByTags();

  if (readingsError || geoError || tagsError) {
    let errorMsg = "";
    if (readingsError) errorMsg = `${T("message.devices-readings")}: ${readingsError.message}`;
    if (geoError) errorMsg = `${T("message.devices-geo-coordinated")}: ${geoError.message}`;
    if (tagsError) errorMsg = `${T("message.devices-by-tags")}: ${tagsError.message}`;
    
    easasAlert({
      summary: T("alert.error"),
      detail: `${T("alert.failed-to-fetch")} ${errorMsg}`,
      severity: "error",
    });
  }

  useEffect(() => {
    if (devicesGeoCoordinates && devicesGeoCoordinates.length > 0) {
      const deviceIds: number[] = devicesGeoCoordinates.map(device => device.properties?.dbId);
      setDevicesIds(deviceIds);

      const firstDeviceCoordinates = devicesGeoCoordinates[0].geometry?.coordinates as LatLngTuple;
      const markerBounds = new L.LatLngBounds([...firstDeviceCoordinates], [...firstDeviceCoordinates]);
      devicesGeoCoordinates.forEach(marker => {
        if (marker.geometry && marker.geometry.coordinates) {
          const { coordinates } = marker.geometry;
          const leafletCoordinateArray = new L.LatLng(coordinates[0], coordinates[1]);
    
          markerBounds.extend(leafletCoordinateArray);
        }
      });
      map.fitBounds(markerBounds);
    }
  }, [devicesGeoCoordinates]);

  useEffect(() => {
    if (devicesByTags) {
      extractDatasets();
    }
  }, [devicesByTags]);


  const extractDatasets = () => {
    const idAndDatasetArray: ExtractedDatasetsProps[] = devicesByTags?.map(device => {
      const datasets: string[] = device.deviceDatasets?.map(dataset => dataset.identifier!) ?? [];
      return { id: device.id ?? 0, datasets };
    }) ?? [];

    setExtractedDatasets(idAndDatasetArray);
  };

  const dialogHandler = (deviceId: number) => {
    const foundDataset = extractedDatasets.find(dataset => dataset.id === deviceId);
    if (foundDataset) {
      const { datasets } = foundDataset;
      setRequiredDatasets(datasets);
      setDisplayDialog(true);
    } else {
      console.error(`No dataset found with id: ${deviceId}`);
    }
  };

  const mappedMarkers = devicesGeoCoordinates?.map(device => {
    const position = device.geometry?.coordinates as LatLngTuple;

    const measurementValueObj = devicesReadings && devicesReadings[device.properties?.dbId];
    const measuredValueText = measurementValueObj
      ? `${roundIfDecimal(measurementValueObj.value)} ${measurementValueObj.unit}`
      : "N\\A";
     
    const icon = divIcon({
      className: "custom-icon",
      html: `<div>${measuredValueText}</div>`,
    });

    const loading = divIcon({
      className: "custom-icon",
      html: `<div><p /></div>`,
    })

    const measurementLabel = isLoading ? loading : icon;

    return (
      <Marker
        key={device.properties?.dbId}
        position={position}
        icon={selectedMeasurementType === "NONE" ? defaultIcon : measurementLabel}
        eventHandlers={{
          click: () => {
            setSelectedTitle(device.properties?.title);
          },
          dblclick: () => dialogHandler(device.properties?.dbId),
        }}
      >
        <Popup className="text-base">{device.properties?.title}</Popup>
      </Marker>
    );
  })

  return (
    <div>
      <MarkerClusterGroup maxClusterRadius={16}>
        {devicesGeoCoordinates && devicesGeoCoordinates.length > 0 && mappedMarkers}
      </MarkerClusterGroup>

      <ZoomControl position="bottomright" />

      <Dropdown
        value={selectedMeasurementType}
        options={measurementOptions}
        className="measurement-input"
        onChange={e => setSelectedMeasurementType(e.value)}
      />

      <Dialog
        header={T("label.device-latest-measurements")}
        visible={displayDialog}
        onHide={() => setDisplayDialog(false)}
        style={{ width: "min(100vw, 500px)" }}
      >
        <DeviceDialogContent datasets={requiredDatasets} title={selectedTitle} />
      </Dialog>
    </div>
  );
};

export default MapContent;
